import { getPathFromNode } from 'utils/helpers';
import {
    INDUSTRIES,
    isCovered,
    isCoveredByAnySource,
} from '@/config/constants';
import useCurrentCountry from './useCurrentCountry';
import { useFetchCountries } from './api';
import useDataSourceToggle from './useDataSourceToggle';
import useRootCategories from './useRootCategories';
import useInstagramData from './useInstagramData';
import useInstagramWeeks from './useInstagramWeeks';
import { usePlan } from './usePlan';
import usePopularity from './usePopularity';
import useGoogleData from './useGoogleData';
import useTikTokData from './useTikTokData';
import useTikTokWeeks from './useTikTokWeeks';

/**
 * The industries a user can navigate to. Three rules and nothing else: the user
 * has access to it, it is an industry, and it is covered in this geo by *any*
 * source. See frontend/CONTRIBUTING.md, "Coverage is a per-source question".
 */
export const useIndustries = () => {
    const country = useCurrentCountry();
    const countriesQuery = useFetchCountries();
    const planQuery = usePlan();
    const rootsQuery = useRootCategories();
    const geo = country?.text_id;

    // An unresolved country is not "nothing is covered": coverage is answered
    // per geo, so `isCoveredByAnySource({geo: undefined})` is false for every
    // industry, and the nav would settle on an empty list mid-flight.
    const industries =
        geo && rootsQuery.data && planQuery.plan?.industry
            ? planQuery.plan.industry
                  .map((rootName) =>
                      rootsQuery.data.find(({ name }) => name === rootName),
                  )
                  .filter(Boolean)
                  .filter(({ name }) => INDUSTRIES.includes(name))
                  .filter(({ name }) =>
                      isCoveredByAnySource({ geo, cat1: name }),
                  )
                  .map((industry) => ({
                      ...industry,
                      to: getPathFromNode(industry),
                      image: industry.image?.[0] || '',
                  }))
            : null;

    // countriesQuery is in here because the answer depends on it; leaving it out
    // lets isLoading go false before the list can be right.
    const queries = [planQuery, rootsQuery, countriesQuery];
    return {
        data: industries,
        isLoading: queries.some(({ isLoading }) => isLoading),
        isError: queries.some(({ isError }) => isError),
        refetch: () =>
            queries.forEach(({ isError, refetch }) => {
                if (isError) refetch();
            }),
    };
};

const useUserIndustries = () => {
    const country = useCurrentCountry();
    const planQuery = usePlan();
    const rootsQuery = useRootCategories();

    const roots =
        rootsQuery.data && planQuery.plan && planQuery.plan.industry
            ? planQuery.plan.industry
                  .map((rootName) =>
                      rootsQuery.data.find(({ name }) => name === rootName),
                  )
                  .filter((v) => v)
            : null;

    const industryDataQuery = useGoogleData({
        enabled: !!(roots && country),
        data: {
            categories: roots ? roots.map(({ id }) => id) : null,
            countries: country ? [country.id] : null,
        },
        paginateBy: 'categories',
    });

    const industriesWithViews = (industryDataQuery.data || []).filter(
        (industry) => industry.hasOwnProperty('volume'),
    );
    const industries = industriesWithViews.length
        ? industriesWithViews
              .map((industry) => ({
                  ...industry,
                  to: getPathFromNode(industry),
                  image:
                      industry.image && industry.image[0]
                          ? industry.image[0]
                          : '',
              }))
              .filter(({ name }) =>
                  isCovered({
                      geo: country?.text_id,
                      dataSource: 'GOOGLE',
                      cat1: name,
                  }),
              )
        : null;

    const queries = [planQuery, rootsQuery, industryDataQuery];
    const isLoading =
        queries.some(({ isLoading }) => isLoading) ||
        (industryDataQuery.data || []).some(
            ({ fetching, fetchingData }) => fetching || fetchingData,
        );
    const isError = queries.some(({ isError }) => isError);
    const refetchAll = () =>
        queries.forEach(({ isError, refetch }) => {
            if (isError) refetch();
        });

    return {
        data: industries,
        isLoading,
        isError,
        refetch: refetchAll,
    };
};

export const useUserIndustriesTikTok = () => {
    const country = useCurrentCountry();
    const planQuery = usePlan();
    const rootsQuery = useRootCategories();

    const roots =
        rootsQuery.data && planQuery.plan && planQuery.plan.industry
            ? planQuery.plan.industry
                  .map((rootName) =>
                      rootsQuery.data.find(({ name }) => name === rootName),
                  )
                  .filter((v) => v)
            : null;

    const paginateBy = 'categories';
    const categories = roots ? roots.map(({ id }) => id) : null;

    const { week, year, query: weeksQuery } = useTikTokWeeks();
    const industryDataQuery = useTikTokData({
        week,
        year,
        paginateBy,
        categories,
        countries: country ? [country.id] : null,
        enabled: !!(country && categories && week && year),
    });

    const sortedIndustries = (industryDataQuery.data || [])
        .map(({ category, ...rest }) => {
            return { ...rest, id: category };
        })
        .sort((a, b) => {
            const aIndex = categories?.findIndex((id) => id === a.id);
            const bIndex = categories?.findIndex((id) => id === b.id);
            return aIndex - bIndex;
        });

    const industriesWithViews = sortedIndustries.filter((industry) =>
        industry.hasOwnProperty('non_cum_avg_weekly_views'),
    );
    const industries = industriesWithViews.length
        ? industriesWithViews
              .map((industry) => ({
                  ...industry,
                  to: getPathFromNode(industry),
                  image:
                      industry.image && industry.image[0]
                          ? industry.image[0]
                          : '',
              }))
              .filter(({ name }) =>
                  isCovered({
                      geo: country?.text_id,
                      dataSource: 'TIKTOK',
                      cat1: name,
                  }),
              )
        : null;

    const queries = [planQuery, rootsQuery, industryDataQuery, weeksQuery];
    const isLoading =
        queries.some(({ isLoading }) => isLoading) ||
        (industryDataQuery.data || []).some(
            ({ fetching, fetchingData }) => fetching || fetchingData,
        );
    const isError = queries.some(({ isError }) => isError);
    const refetchAll = () =>
        queries.forEach(({ isError, refetch }) => {
            if (isError) refetch();
        });

    return {
        data: industries,
        isLoading,
        isError,
        refetch: refetchAll,
    };
};

export const useSubCategoriesTikTok = ({ parent, country }) => {
    const data = {
        categories: parent ? parent.children : null,
        countries: country ? [country.id] : null,
    };
    const paginateBy = 'categories';
    const { week, year, query: weeksQuery } = useTikTokWeeks();
    const tikTokDataQuery = useTikTokData({
        week,
        year,
        paginateBy,
        ...data,
        enabled: !!(week && year && data.countries && data.categories),
    });

    if (weeksQuery.isSuccess && !weeksQuery.data) {
        return {
            isLoading: false,
            data: [],
        };
    }

    return {
        ...tikTokDataQuery,
        isLoading: tikTokDataQuery.isLoading || weeksQuery.isLoading,
        data: tikTokDataQuery.data
            ? tikTokDataQuery.data.map(({ category, ...rest }) => ({
                  ...rest,
                  id: category,
              }))
            : tikTokDataQuery.data,
    };
};

export const useUserIndustriesInstagram = () => {
    const country = useCurrentCountry();
    const planQuery = usePlan();
    const rootsQuery = useRootCategories();

    const roots =
        rootsQuery.data && planQuery.plan && planQuery.plan.industry
            ? planQuery.plan.industry
                  .map((rootName) =>
                      rootsQuery.data.find(({ name }) => name === rootName),
                  )
                  .filter((v) => v)
            : null;

    const paginateBy = 'categories';
    const categories = roots ? roots.map(({ id }) => id) : null;

    const { week, year, query: weeksQuery } = useInstagramWeeks();

    const industryDataQuery = useInstagramData({
        week,
        year,
        paginateBy,
        categories,
        countries: country ? [country.id] : null,
        enabled: !!(country && categories && week && year),
    });

    const sortedIndustries = (industryDataQuery.data || [])
        .map(({ category, ...rest }) => {
            return { ...rest, id: category };
        })
        .sort((a, b) => {
            const aIndex = categories?.findIndex((id) => id === a.id);
            const bIndex = categories?.findIndex((id) => id === b.id);
            return aIndex - bIndex;
        });

    const industriesWithLikes = sortedIndustries.filter((industry) =>
        industry.hasOwnProperty('non_cum_avg_weekly_likes'),
    );
    const industries = industriesWithLikes.length
        ? industriesWithLikes
              .map((industry) => ({
                  ...industry,
                  to: getPathFromNode(industry),
                  image:
                      industry.image && industry.image[0]
                          ? industry.image[0]
                          : '',
              }))
              .filter(({ name }) =>
                  isCovered({
                      geo: country?.text_id,
                      dataSource: 'INSTAGRAM',
                      cat1: name,
                  }),
              )
        : null;

    const queries = [planQuery, rootsQuery, industryDataQuery, weeksQuery];
    const isLoading =
        queries.some(({ isLoading }) => isLoading) ||
        (industryDataQuery.data || []).some(
            ({ fetching, fetchingData }) => fetching || fetchingData,
        );
    const isError = queries.some(({ isError }) => isError);
    const refetchAll = () =>
        queries.forEach(({ isError, refetch }) => {
            if (isError) refetch();
        });

    return {
        data: industries,
        isLoading,
        isError,
        refetch: refetchAll,
    };
};

export const useSubCategoriesInstagram = ({ parent, country }) => {
    const data = {
        categories: parent ? parent.children : null,
        countries: country ? [country.id] : null,
    };
    const paginateBy = 'categories';
    const { week, year, query: weeksQuery } = useInstagramWeeks();
    const instagramDataQuery = useInstagramData({
        week,
        year,
        paginateBy,
        ...data,
        enabled: !!(week && year && data.countries && data.categories),
    });

    if (weeksQuery.isSuccess && !weeksQuery.data) {
        return {
            isLoading: false,
            data: [],
        };
    }

    return {
        ...instagramDataQuery,
        isLoading: instagramDataQuery.isLoading || weeksQuery.isLoading,
        data: instagramDataQuery.data
            ? instagramDataQuery.data.map(({ category, ...rest }) => ({
                  ...rest,
                  id: category,
              }))
            : instagramDataQuery.data,
    };
};

export const useSubCategoriesOverviewV2 = ({ parent, country }) => {
    const paginateBy = 'categories';
    return usePopularity({
        paginateBy,
        categories: parent ? parent.children : null,
        countries: country ? [country.id] : null,
        enabled: !!(country && parent?.children),
    });
};

export const useUserIndustriesOverviewV2 = () => {
    const country = useCurrentCountry();
    const planQuery = usePlan();
    const rootsQuery = useRootCategories();

    const roots =
        rootsQuery.data && planQuery.plan && planQuery.plan.industry
            ? planQuery.plan.industry
                  .map((rootName) =>
                      rootsQuery.data.find(({ name }) => name === rootName),
                  )
                  .filter((v) => v)
            : null;

    const paginateBy = 'categories';
    const categories = roots ? roots.map(({ id }) => id) : null;

    const industryDataQuery = usePopularity({
        paginateBy,
        categories,
        countries: country ? [country.id] : null,
        enabled: !!(country && categories),
    });

    const sortedIndustries = (industryDataQuery.data || [])
        .slice()
        .sort((a, b) => {
            const aIndex = categories?.findIndex((id) => id === a.id);
            const bIndex = categories?.findIndex((id) => id === b.id);
            return aIndex - bIndex;
        });

    const industries = sortedIndustries.length
        ? sortedIndustries
              .map((industry) => ({
                  ...industry,
                  to: getPathFromNode(industry),
                  image:
                      industry.image && industry.image[0]
                          ? industry.image[0]
                          : '',
              }))
              .filter(({ name }) =>
                  isCovered({
                      geo: country?.text_id,
                      dataSource: 'POPULARITY',
                      cat1: name,
                  }),
              )
        : null;

    const queries = [planQuery, rootsQuery, industryDataQuery];
    const isLoading = queries.some(({ isLoading }) => isLoading);
    const isError = queries.some(({ isError }) => isError);
    const refetchAll = () =>
        queries.forEach(({ isError, refetch }) => {
            if (isError) refetch();
        });

    return {
        data: industries,
        isLoading,
        isError,
        refetch: refetchAll,
    };
};

export default useUserIndustries;
